import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { GaugeCircle, Settings, RefreshCw } from "lucide-react";
import { getVesselAuxEngineLoadAndPowerData } from "../../../../services/vesselMachineryService";

const engineOptions = [
  { id: "ae1", name: "AE1", color: "#1a5276", bgColor: "#1a53761a" },
  { id: "ae2", name: "AE2", color: "#d35400", bgColor: "#d354001a" },
  { id: "ae3", name: "AE3", color: "#27ae60", bgColor: "#27ae601a" },
  { id: "ae4", name: "AE4", color: "#7d3c98", bgColor: "#7d3c981a" },
  { id: "ae5", name: "AE5", color: "#a93226", bgColor: "#a932261a" },
  { id: "ae6", name: "AE6", color: "#b7950b", bgColor: "#b7950b1a" },
];

const CircularProgressBar = ({ percentage, color }) => {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative w-32 h-32 flex items-center justify-center">
      <svg className="w-full h-full" viewBox="0 0 100 100">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="transparent"
          stroke="#edeef0ff"
          strokeWidth="8"
        />
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="transparent"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 50 50)"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <span className="text-2xl font-bold text-gray-800">{percentage}</span>
          <span className="text-sm text-gray-500">%</span>
        </div>
      </div>
    </div>
  );
};

const AuxiliaryEngineData = () => {
  const selectedAE = useSelector((state) => state.ae.selectedAE);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { selectedVoyage } = useSelector((state) => state.voyageFilter);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadData, setLoadData] = useState({
    loadValue: 0, // ratio from API
    maxCapacity: 0, // kW from API
    loadKW: 0, // calculated kW value
    loadPercentage: 0, // calculated percentage
  });

  const fetchLoadData = async () => {
    if (!currentVessel?._id || !selectedAE) return;

    setIsRefreshing(true);

    try {
      const response = await getVesselAuxEngineLoadAndPowerData(
        currentVessel._id,
        selectedAE,
        selectedVoyage ? null : startDate,
        selectedVoyage ? null : endDate,
        selectedVoyage?._id || null
      );

      console.log("getVesselAuxEngineLoadAndPowerData: ", response);

      if (response?.status === "success") {
        const loadData = response.data;
        const loadPercentage = Math.min(loadData.loadValue, 100);

        setLoadData({
          loadValue: loadData.loadValue,
          maxCapacity: loadData.maxCapacity,
          loadKW: Math.round(loadData.loadValue * 100) / 100,
          loadPercentage: Math.round(loadPercentage * 100) / 100,
        });

      }
    } catch (err) {
      console.error("Error fetching AE load data:", err);
      setLoadData({
        loadValue: 0,
        maxCapacity: 0,
        loadKW: 0,
        loadPercentage: 0,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLoadData();
  }, [currentVessel, selectedAE, startDate, endDate, selectedVoyage]);

  const handleRefresh = () => {
    fetchLoadData();
  };

  const currentEngine =
    engineOptions.find((engine) => engine.id === selectedAE) ||
    engineOptions[0];

  return (
    <div className="w-[50%] flex flex-col bg-white rounded-2xl shadow-xs border border-gray-100">
      <div className="px-6 pt-5 pb-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-[#e1f5fb] text-[#27ade3] rounded-md">
            <Settings className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-semibold text-[#0a2540] tracking-tight">
            Auxiliary Engine Data
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
            disabled={isRefreshing}
            aria-label="Refresh data"
          >
            <RefreshCw
              className={`w-4 h-4 text-gray-500 ${
                isRefreshing ? "animate-spin" : ""
              }`}
            />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 p-6">
        <div className="h-full flex justify-center items-center">
          <div
            className="rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
            style={{
              backgroundColor: currentEngine.bgColor,
              width: "100%",
              maxWidth: "22rem",
              height: "100%",
              maxHeight: "18rem",
            }}
          >
            <div className="p-5 flex-grow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div
                    className="p-2 rounded-lg backdrop-blur-sm"
                    style={{
                      backgroundColor: `${currentEngine.color}20`,
                      boxShadow: `inset 0 0 0 1px ${currentEngine.color}30`,
                    }}
                  >
                    <GaugeCircle
                      className="w-4 h-4"
                      style={{ color: currentEngine.color }}
                    />
                  </div>
                  <h3 className="text-sm font-medium text-gray-900">
                    {currentEngine.name} Load
                  </h3>
                </div>
                {/* <span
                  className="text-sm font-medium px-2 py-1 rounded-full backdrop-blur-sm"
                  style={{
                    backgroundColor: `${currentEngine.color}15`,
                    color: currentEngine.color,
                    boxShadow: `inset 0 0 0 1px ${currentEngine.color}20`,
                  }}
                >
                  <span className="font-bold">{loadData.loadKW}</span> kW
                </span> */}
              </div>

              <div className="flex items-center justify-center">
                <CircularProgressBar
                  percentage={loadData.loadPercentage}
                  color={currentEngine.color}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuxiliaryEngineData;
